import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import { currentLocale } from '../../i18n';

/**
 * Admin panel: player search and management.
 */
export default function AdminPlayers({ perms }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Register form
  const [showRegister, setShowRegister] = useState(false);
  const [regDiscordId, setRegDiscordId] = useState('');
  const [regDiscordUsername, setRegDiscordUsername] = useState('');
  const [regSteamUrl, setRegSteamUrl] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [regResult, setRegResult] = useState(null);
  const [regError, setRegError] = useState('');

  // Pending registrations (Discord-logged-in users without Steam link)
  const [pending, setPending] = useState([]);

  // Selected registered player (opens detail modal)
  const [editing, setEditing] = useState(null);
  const [unlinkBusy, setUnlinkBusy] = useState(false);
  const [unlinkError, setUnlinkError] = useState('');

  const fetchPlayers = useCallback(async () => {
    if (!perms.guildId) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/players/${perms.guildId}`, {
        headers: getAuthHeaders(),
      });
      if (r.ok) {
        const data = await r.json();
        setPlayers(Array.isArray(data) ? data : data.results || []);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [perms.guildId, getAuthHeaders]);

  const fetchPending = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/players/pending`, {
        headers: getAuthHeaders(),
      });
      if (r.ok) {
        const data = await r.json();
        setPending(Array.isArray(data.pending) ? data.pending : []);
      }
    } catch {
      // silent
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchPlayers();
    fetchPending();
  }, [fetchPlayers, fetchPending]);

  const prefillFromPending = (p) => {
    setRegDiscordId(p.discord_user_id);
    setRegDiscordUsername(p.discord_username || '');
    setRegSteamUrl('');
    setRegResult(null);
    setRegError('');
    setShowRegister(true);
  };

  /**
   * Prefill the register form with an existing player's Discord info so the
   * admin can re-register them against a different Steam profile.
   */
  const prefillFromEditing = (p) => {
    setRegDiscordId(p.discord_id || '');
    setRegDiscordUsername(p.discord_username || '');
    setRegSteamUrl('');
    setRegResult(null);
    setRegError('');
    setShowRegister(true);
    setEditing(null);
    // Scroll the register card into view
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /**
   * Remove the Steam link from the selected player. Player + history is
   * preserved; they reappear in the "Väntar på Steam-koppling" list.
   */
  const handleUnlink = async () => {
    if (!editing) return;
    if (!window.confirm(
      t('admin.playerUnlinkConfirm', { name: editing.steam_username || editing.discord_username })
    )) {
      return;
    }
    setUnlinkBusy(true);
    setUnlinkError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/players/unlink`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ discord_user_id: editing.discord_id }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setUnlinkError(data.error || data.detail || t('admin.playerUnlinkFailed'));
        return;
      }
      setEditing(null);
      fetchPlayers();
      fetchPending();
    } catch {
      setUnlinkError(t('settings.networkError'));
    } finally {
      setUnlinkBusy(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegLoading(true);
    setRegError('');
    setRegResult(null);
    try {
      const body = {
        discord_user_id: regDiscordId,
        steam_profile_url: regSteamUrl,
      };
      if (regDiscordUsername) {
        body.discord_username = regDiscordUsername;
      }
      const r = await fetch(`${API_URL}/api/admin/players/register`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) {
        setRegError(data.error || data.detail || t('admin.playerRegisterFailed'));
      } else {
        setRegResult(data);
        setRegDiscordId('');
        setRegDiscordUsername('');
        setRegSteamUrl('');
        fetchPlayers();
        fetchPending();
      }
    } catch {
      setRegError(t('settings.networkError'));
    } finally {
      setRegLoading(false);
    }
  };

  const filtered = players
    .filter((p) => {
      if (!search) return true;
      const q = search.toLowerCase();
      const name = (p.steam_username || p.discord_username || '').toLowerCase();
      const steamId = p.steam_id || '';
      return name.includes(q) || steamId.includes(q);
    })
    .sort((a, b) =>
      (a.steam_username || a.discord_username || '').localeCompare(
        b.steam_username || b.discord_username || '',
        currentLocale(),
        { sensitivity: 'base' },
      ),
    );

  const sortedPending = [...pending].sort((a, b) =>
    (a.discord_username || a.username || '').localeCompare(
      b.discord_username || b.username || '',
      currentLocale(),
      { sensitivity: 'base' },
    ),
  );

  return (
    <div>
      <h1 className="admin-section-title">{t('nav.players')}</h1>

      {/* Pending registrations */}
      {perms.hasCommand('manager.register') && pending.length > 0 && (
        <div className="admin-card">
          <div className="admin-card-title">
            {t('admin.playerPendingTitle', { count: pending.length })}
          </div>
          <div className="admin-card-desc">
            {t('admin.playerPendingDesc')}
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Discord</th>
                <th>Discord ID</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedPending.map((p) => (
                <tr key={p.user_id}>
                  <td>{p.discord_username || p.username || '-'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {p.discord_user_id}
                  </td>
                  <td>
                    <button
                      className="admin-btn admin-btn-secondary"
                      type="button"
                      onClick={() => prefillFromPending(p)}
                    >
                      {t('admin.playerAddSteam')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Register */}
      {perms.hasCommand('manager.register') && (
        <div className="admin-card">
          <div className="admin-card-title">{t('admin.playerRegisterTitle')}</div>
          <div className="admin-card-desc">
            {t('admin.playerRegisterDesc')}
          </div>
          {!showRegister ? (
            <button className="admin-btn" onClick={() => setShowRegister(true)}>
              {t('admin.playerRegister')}
            </button>
          ) : (
            <form onSubmit={handleRegister}>
              <div className="admin-row">
                <label className="admin-label">Discord ID</label>
                <input
                  className="admin-input"
                  placeholder={t('admin.playerDiscordIdPlaceholder')}
                  value={regDiscordId}
                  onChange={(e) => setRegDiscordId(e.target.value)}
                  required
                />
              </div>
              {regDiscordUsername && (
                <div className="admin-row">
                  <label className="admin-label">{t('admin.playerDiscordNameLabel')}</label>
                  <input
                    className="admin-input"
                    value={regDiscordUsername}
                    onChange={(e) => setRegDiscordUsername(e.target.value)}
                    readOnly
                    style={{ opacity: 0.75 }}
                  />
                </div>
              )}
              <div className="admin-row">
                <label className="admin-label">Steam URL</label>
                <input
                  className="admin-input"
                  placeholder="https://steamcommunity.com/id/..."
                  value={regSteamUrl}
                  onChange={(e) => setRegSteamUrl(e.target.value)}
                  required
                />
              </div>
              <div className="admin-row">
                <button className="admin-btn" type="submit" disabled={regLoading}>
                  {regLoading ? t('admin.playerRegistering') : t('admin.playerRegister')}
                </button>
                <button
                  className="admin-btn admin-btn-secondary"
                  type="button"
                  onClick={() => {
                    setShowRegister(false);
                    setRegDiscordUsername('');
                  }}
                >
                  {t('matchDetail.cancel')}
                </button>
                {regResult && (
                  <span className="admin-success">
                    {t('admin.playerRegistered', { name: regResult.steam_username || regResult.username })}
                    {regResult.was_updated ? t('admin.playerUpdatedSuffix') : ''}
                  </span>
                )}
                {regError && <span className="admin-error">{regError}</span>}
              </div>
            </form>
          )}
        </div>
      )}

      {/* Player list */}
      <div className="admin-card">
        <div className="admin-card-title">
          {t('admin.playerRegisteredList', { count: filtered.length })}
        </div>
        <div className="admin-card-desc">
          {t('admin.playerListDesc')}
        </div>
        <div className="admin-search">
          <input
            placeholder={t('admin.playerSearchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {loading ? (
          <div className="admin-info">{t('live.loading')}</div>
        ) : (
          <table className="admin-table admin-table-clickable">
            <thead>
              <tr>
                <th>{t('admin.nameLabel')}</th>
                <th>SteamID64</th>
                <th>Discord</th>
                <th>{t('admin.accessTitle')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  onClick={() => setEditing(p)}
                  className="admin-row-clickable"
                >
                  <td>{p.steam_username || p.discord_username || '-'}</td>
                  <td className="admin-mono-cell">{p.steam_id || '-'}</td>
                  <td>{p.discord_username || '-'}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <AccessStatusPill status={p.access_status} onClick={() => navigate('/webadmin/access')} />
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="admin-empty-cell">
                    {t('admin.playerNotFound')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <PlayerDetailModal
          player={editing}
          onClose={() => {
            setEditing(null);
            setUnlinkError('');
          }}
          onEditSteam={() => prefillFromEditing(editing)}
          onUnlink={handleUnlink}
          busy={unlinkBusy}
          error={unlinkError}
          canManage={perms.hasCommand('manager.register')}
        />
      )}
    </div>
  );
}

/**
 * Modal showing a registered player's identity and admin actions:
 * open stats profile, change Steam link, or unlink Steam entirely.
 */
function PlayerDetailModal({ player, onClose, onEditSteam, onUnlink, busy, error, canManage }) {
  const { t } = useTranslation();
  const displayName = player.steam_username || player.discord_username || t('nav.players');
  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal admin-perm-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="admin-perm-modal-title">{displayName}</h3>
        <div className="admin-perm-modal-sub">
          {player.discord_username || '-'} · {player.discord_id || '-'}
        </div>

        <div className="admin-perm-modal-section">
          <div className="admin-perm-modal-heading">{t('admin.connection')}</div>
          <div className="admin-user-detail">
            Steam: {player.steam_username || '-'}
          </div>
          <div className="admin-user-detail">
            SteamID64: <span className="admin-mono-inline">{player.steam_id || '-'}</span>
          </div>
          {player.elo && (
            <div className="admin-user-detail">
              ELO: {player.elo.elo} · {player.elo.rank}
            </div>
          )}
        </div>

        {player.steam_id && (
          <div className="admin-perm-modal-section">
            <a
              className="admin-btn admin-btn-secondary"
              href={`/stats/players/${player.steam_id}`}
              target="_blank"
              rel="noreferrer"
            >
              {t('admin.playerOpenProfile')}
            </a>
          </div>
        )}

        {canManage && (
          <div className="admin-perm-modal-section">
            <div className="admin-perm-modal-heading">{t('admin.newsColActions')}</div>
            <div className="admin-confirm-actions">
              <button
                className="admin-btn"
                type="button"
                onClick={onEditSteam}
                disabled={busy}
              >
                {t('admin.playerChangeSteam')}
              </button>
              <button
                className="admin-btn admin-btn-danger"
                type="button"
                onClick={onUnlink}
                disabled={busy}
              >
                {busy ? t('admin.removing') : t('admin.playerUnlink')}
              </button>
            </div>
          </div>
        )}

        {error && <div className="admin-error">{error}</div>}

        <div className="admin-confirm-actions">
          <button
            className="admin-btn admin-btn-secondary"
            type="button"
            onClick={onClose}
            disabled={busy}
          >
            {t('profile.close')}
          </button>
        </div>
      </div>
    </div>
  );
}


// access_status value -> { labelKey (resolved via t() in the pill), css class }.
// 'active' reuses the existing admin.pillActive key.
const ACCESS_STATUS_LABELS = {
  active:    { labelKey: 'admin.pillActive',         cls: 'access-pill-active' },
  pending:   { labelKey: 'admin.accessPillPending',   cls: 'access-pill-pending' },
  suspended: { labelKey: 'admin.accessPillSuspended', cls: 'access-pill-suspended' },
  banned:    { labelKey: 'admin.accessPillBanned',    cls: 'access-pill-banned' },
};

/**
 * Liten färgad pill som visar Player.access_status från Sprint 1.
 * Klick navigerar till /admin/access för full hantering.
 */
function AccessStatusPill({ status, onClick }) {
  const { t } = useTranslation();
  const meta = ACCESS_STATUS_LABELS[status];
  const label = meta ? t(meta.labelKey) : (status || '—');
  return (
    <button
      type="button"
      className={`access-pill ${meta ? meta.cls : 'access-pill-unknown'}`}
      onClick={onClick}
      title={t('admin.manageAccess')}
    >
      {label}
    </button>
  );
}
